import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useIdentity } from "../identity/useIdentity";
import { logger } from "../logging/logger";
import { useProject } from "../project/ProjectContext";
import { ChecklistSelector } from "./ChecklistSelector";
import { type ResolvedFilter } from "./DateFilterInput";
import { type SubmitJobRequest } from "./jobsClient";
import { type ParseSubjectsResult } from "./parseSubjects";
import { SubjectInputModeTabs } from "./SubjectInputModeTabs";
import { useSubmitJob } from "./useJobs";

interface ChecklistChoice {
    id: string;
    name: string;
}

const EMPTY_PARSE: ParseSubjectsResult = {
    valid: [],
    duplicateCount: 0,
    invalid: [],
    totalRows: 0,
};

interface FormDraft {
    subjectsText: string;
    jobName: string;
    checklist: ChecklistChoice | null;
}

// Per-project sessionStorage key. Different projects keep separate drafts;
// switching projects (which clears project state per PRD-004) also drops
// the prior project's draft, since the key changes.
function draftKey(projectId: string | null): string | null {
    return projectId ? `checklist-results:export-form-draft:${projectId}` : null;
}

function loadDraft(projectId: string | null): FormDraft | null {
    const key = draftKey(projectId);
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as FormDraft;
        if (typeof parsed.subjectsText !== "string") return null;
        return parsed;
    } catch {
        return null;
    }
}

function saveDraft(projectId: string | null, draft: FormDraft): void {
    const key = draftKey(projectId);
    if (!key) return;
    try {
        sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {
        // sessionStorage quota or disabled; ignore — draft simply doesn't persist.
    }
}

function clearDraft(projectId: string | null): void {
    const key = draftKey(projectId);
    if (!key) return;
    try {
        sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
}

/**
 * Top-level export-config form. Composes:
 * - SubjectInput (file upload + paste, validates UUIDs and surfaces
 *   the count-loaded / duplicates-removed / invalid-rows summary).
 * - ChecklistSelector (lists project checklists).
 * - Optional job name (default: org / project / checklist / N subjects).
 *
 * On submit, posts to the jobs API. The submission body includes the
 * `projectId` (required by the API Lambda's body validation; it's NOT
 * sent as a header — that's a patient-registry convention, not a jobs-API
 * one) and the human-readable names so the API can build the default
 * job name without re-fetching `/identity`.
 *
 * Validation stops at UUID parsing. We deliberately do NOT pre-check
 * each pasted UUID against the patient-registry — see PRD-001 §"Pre-flight
 * existence check (rejected)" and GH issue #10. Submitting unknown subject
 * IDs records each as `subject-not-found` in the failures sidecar at job
 * runtime; the user sees them there.
 */
export function ExportConfigForm() {
    const navigate = useNavigate();
    const { selectedProjectId, authorizedProjects } = useProject();
    const { identity } = useIdentity();
    const submit = useSubmitJob();
    const initialDraft = useMemo(() => loadDraft(selectedProjectId), [selectedProjectId]);
    const [parsed, setParsed] = useState<ParseSubjectsResult>(EMPTY_PARSE);
    // PRD-001 rev 13: filter mode carries the resolved filter, not a subject
    // list. Exactly one of `parsed.valid` / `filter` is non-empty at submit —
    // the mode tabs clear both on a switch (backend re-enforces the XOR).
    const [filter, setFilter] = useState<ResolvedFilter | null>(null);
    const [subjectsText, setSubjectsText] = useState<string>(initialDraft?.subjectsText ?? "");
    const [checklist, setChecklist] = useState<ChecklistChoice | null>(initialDraft?.checklist ?? null);
    const [jobName, setJobName] = useState<string>(initialDraft?.jobName ?? "");

    const handleParsed = useCallback((result: ParseSubjectsResult) => {
        setParsed(result);
    }, []);

    const handleFilterChanged = useCallback((next: ResolvedFilter | null) => {
        setFilter(next);
    }, []);

    const handleChecklist = useCallback((id: string, name: string) => {
        setChecklist({ id, name });
    }, []);

    const handleSubjectsTextChange = useCallback((text: string) => {
        setSubjectsText(text);
    }, []);

    // Persist the draft on every input change so navigation away and back
    // doesn't drop a thousand pasted UUIDs (PRD-001 customer scenario).
    useEffect(() => {
        if (!selectedProjectId) return;
        saveDraft(selectedProjectId, { subjectsText, jobName, checklist });
    }, [selectedProjectId, subjectsText, jobName, checklist]);

    const projectName = authorizedProjects.find((p) => p.id === selectedProjectId)?.name;
    const orgName = identity?.organization.name;

    // Rev 13 submit gate: a pasted/uploaded list OR a valid filter (the mode
    // tabs guarantee at most one is present).
    const canSubmit =
        selectedProjectId !== null &&
        checklist !== null &&
        (parsed.valid.length > 0 || filter !== null) &&
        !submit.isPending;

    const doSubmit = useCallback(async () => {
        if (!selectedProjectId || !checklist) return;
        const common = {
            checklistId: checklist.id,
            projectId: selectedProjectId,
            jobName: jobName.trim() || undefined,
            organizationName: orgName,
            projectName: projectName,
            checklistName: checklist.name,
        };
        const body: SubmitJobRequest =
            parsed.valid.length > 0 ? { ...common, subjectIds: parsed.valid } : { ...common, filter: filter ?? {} };
        try {
            const result = await submit.mutateAsync(body);
            logger.info("export submitted", { jobId: result.jobId });
            clearDraft(selectedProjectId);
            navigate("/jobs");
        } catch (err) {
            logger.error("export submit failed", err);
        }
    }, [submit, selectedProjectId, checklist, jobName, orgName, projectName, navigate, parsed, filter]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!canSubmit || !selectedProjectId || !checklist) return;
        await doSubmit();
    };

    return (
        <form onSubmit={handleSubmit} data-testid="export-config-form" className="export-form">
            <div className="page-header">
                <div>
                    <div className="page-title">Bulk checklist export</div>
                    <div className="page-help">
                        Pick a checklist, supply the subject UUIDs, and submit. The job runs in
                        the background — you can close this tab and come back to{" "}
                        <Link to="/jobs">job history</Link> later.
                    </div>
                </div>
            </div>

            <fieldset>
                <legend>Subjects</legend>
                <SubjectInputModeTabs
                    onParsed={handleParsed}
                    onFilterChanged={handleFilterChanged}
                    initialPasteText={subjectsText}
                    onPasteTextChange={handleSubjectsTextChange}
                />
            </fieldset>

            <fieldset>
                <legend>Checklist</legend>
                <ChecklistSelector
                    selectedChecklistId={checklist?.id ?? null}
                    onSelect={handleChecklist}
                />
            </fieldset>

            <fieldset>
                <legend>Job name (optional)</legend>
                <div className="field">
                    <input
                        type="text"
                        className="f-input"
                        value={jobName}
                        onChange={(e) => setJobName(e.target.value)}
                        placeholder={defaultJobNamePlaceholder({
                            orgName,
                            projectName,
                            checklistName: checklist?.name,
                            count: parsed.valid.length,
                            filter,
                        })}
                        data-testid="job-name-input"
                    />
                </div>
            </fieldset>

            {submit.isError && submit.error && (
                <div className="callout callout-error" data-testid="submit-error">
                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    <span>Submission failed: {submit.error.message}</span>
                </div>
            )}

            <div className="form-actions">
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!canSubmit}
                    data-testid="submit-export"
                >
                    {submit.isPending ? (
                        <>
                            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />{" "}
                            Submitting…
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Submit
                            export
                        </>
                    )}
                </button>
            </div>

        </form>
    );
}

function defaultJobNamePlaceholder(parts: {
    orgName?: string;
    projectName?: string;
    checklistName?: string;
    count: number;
    filter: ResolvedFilter | null;
}): string {
    const org = parts.orgName ?? "org";
    const proj = parts.projectName ?? "project";
    const checklist = parts.checklistName ?? "checklist";
    // Rev 13: filter jobs have no count at submit — the descriptor is the
    // label (CSMs name batches after labels) or the resolved instant; the
    // backend rewrites system-defaulted names to the full `{count} subjects`
    // template once gathering completes.
    if (parts.filter) {
        const descriptor = parts.filter.label ?? parts.filter.createdStart ?? "filter";
        return `${org} / ${proj} / ${checklist} / ${descriptor}`;
    }
    return `${org} / ${proj} / ${checklist} / ${parts.count} subjects`;
}
